import { Activity, ChevronDown, HelpCircle, LogOut } from 'lucide-react';
import type { ElementType, ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { NotificationBell } from '@/components/NotificationBell';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';

export interface NavItem {
  to: string;
  label: string;
  icon: ElementType;
  end?: boolean;
  /**
   * Extra path prefixes that should also mark this nav item active. Useful for
   * sibling sub-flows that live at a different path than the nav item itself
   * (e.g. `/resident/invite` belongs under the `Invitations` item).
   */
  activeMatch?: string[];
}

export interface NavSection {
  label?: string;
  items: NavItem[];
}

export interface PortalMenuItem {
  to: string;
  label: string;
  icon: ElementType;
  onClick?: () => void;
}

export interface PortalLayoutProps {
  brand: ReactNode;
  navSections: NavSection[];
  notificationsTo: string;
  activeClassName?: string;
  inactiveClassName?: string;
  headerContent?: ReactNode;
  menuItems?: PortalMenuItem[];
  /**
   * Optional phone-native bottom tab bar. When provided, it renders fixed to the
   * bottom of the viewport below the mobile breakpoint, and the main content
   * gains bottom padding so it is not obscured. Desktop is unaffected.
   */
  mobileNav?: ReactNode;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Sidebar navigation list. Rendered inside `SidebarProvider` so it can read the
 * mobile sheet state and dismiss the off-canvas sidebar the moment a resident
 * taps a destination — otherwise the sheet stays open on top of the page and
 * the user has to tap the backdrop to reveal the page they just navigated to.
 */
function SidebarNav({
  navSections,
  activeClassName,
  inactiveClassName,
}: {
  navSections: NavSection[];
  activeClassName: string;
  inactiveClassName: string;
}) {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();

  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <>
      {navSections.map((section, i) => (
        <SidebarGroup key={section.label ?? i}>
          {section.label && <SidebarGroupLabel>{section.label}</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) => {
                // NavLink's function `className` cannot be used here because
                // the Radix `Slot` behind `SidebarMenuButton asChild`
                // stringifies a function prop instead of invoking it, which
                // silently breaks the active highlight. Compute the active
                // state explicitly and pass a plain string instead.
                const isActive =
                  item.end === true
                    ? location.pathname === item.to
                    : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                const matchedSibling = item.activeMatch?.some(
                  (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
                );
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.to}
                        end={item.end === true}
                        onClick={closeOnMobile}
                        className={isActive || matchedSibling ? activeClassName : inactiveClassName}
                      >
                        <item.icon className="size-4" />
                        <span>{item.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </>
  );
}

export function PortalLayout({
  brand,
  navSections,
  notificationsTo,
  activeClassName = 'text-sidebar-primary-foreground bg-sidebar-accent',
  inactiveClassName = '',
  headerContent,
  menuItems = [],
  mobileNav,
}: PortalLayoutProps) {
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();
  const showMobileNav = isMobile && !!mobileNav;

  const handleSignOut = () => {
    void signOut();
  };

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>{brand}</SidebarHeader>

        <SidebarContent>
          <SidebarNav
            navSections={navSections}
            activeClassName={activeClassName}
            inactiveClassName={inactiveClassName}
          />
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => {
                  if (window.Intercom) {
                    window.Intercom('show');
                  } else {
                    window.open('mailto:support@securelabsystems.com', '_blank');
                  }
                }}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <HelpCircle className="size-4" />
                <span>Help &amp; Support</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.open('https://status.securelabsystems.com', '_blank')}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <Activity className="size-4" />
                <span>System Status</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg">
                    <Avatar className="size-8 shrink-0">
                      <AvatarFallback className="bg-primary/20 text-[var(--avatar-initials-fg)] text-xs font-semibold">
                        {user ? initials(user.fullName) : '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex flex-col items-start min-w-0 flex-1">
                      <span className="truncate text-sm font-medium">{user?.fullName}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                    </span>
                    <ChevronDown className="size-4 ml-auto shrink-0" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium">{user?.fullName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  {menuItems.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {menuItems.map((item) =>
                          item.onClick ? (
                            <DropdownMenuItem
                              key={item.to}
                              onClick={item.onClick}
                              className="cursor-pointer"
                            >
                              <item.icon className="mr-2 size-4" />
                              {item.label}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem key={item.to} asChild>
                              <Link to={item.to}>
                                <item.icon className="mr-2 size-4" />
                                {item.label}
                              </Link>
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuGroup>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => void handleSignOut()}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 size-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
          <SidebarTrigger className="-ml-1" />
          {headerContent ?? <div className="flex-1" />}
          <NotificationBell to={notificationsTo} />
          {/* F2 fix: Figma shows avatar in header alongside bell */}
          <Avatar className="size-7 shrink-0">
            <AvatarFallback className="bg-primary/20 text-[var(--avatar-initials-fg)] text-[10px] font-semibold">
              {user ? initials(user.fullName) : '?'}
            </AvatarFallback>
          </Avatar>
        </header>
        <main className={`flex-1 overflow-auto p-6 ${showMobileNav ? 'pb-24' : ''}`}>
          <Outlet />
        </main>
      </SidebarInset>
      {showMobileNav && mobileNav}
    </SidebarProvider>
  );
}
